import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { TICKET_BUCKETS, type TicketBucket } from "../lib/api/types";
import type {
  AgreementRateByBucketPoint,
  AgreementRatePoint,
  CreditCultureSeries,
  IndustryCode,
} from "../lib/api/types";

const AXIS = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };
const TOOLTIP = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0px",
  color: "hsl(var(--popover-foreground))",
  fontSize: 12,
};
const LEGEND = { fontSize: 11, color: "hsl(var(--muted-foreground))" };

const pct = (value: number) => `${value}%`;

/** chart-1..chart-5 plus three derived shades so all 8 buckets stay distinguishable. */
export const BUCKET_COLORS: Record<TicketBucket, string> = {
  "0-10K": "hsl(var(--chart-1))",
  "10-25K": "hsl(var(--chart-2))",
  "25-50K": "hsl(var(--chart-3))",
  "50-100K": "hsl(var(--chart-4))",
  "100-150K": "hsl(var(--chart-5))",
  "150-200K": "color-mix(in oklch, hsl(var(--chart-1)) 55%, hsl(var(--foreground)))",
  "200-250K": "color-mix(in oklch, hsl(var(--chart-2)) 55%, hsl(var(--foreground)))",
  "Over 250K": "color-mix(in oklch, hsl(var(--chart-3)) 55%, hsl(var(--foreground)))",
};

const BUCKET_LABELS: Record<TicketBucket, string> = {
  "0-10K": "$0–10K",
  "10-25K": "$10–25K",
  "25-50K": "$25–50K",
  "50-100K": "$50–100K",
  "100-150K": "$100–150K",
  "150-200K": "$150–200K",
  "200-250K": "$200–250K",
  // Open-ended top bucket — no upper bound to print.
  "Over 250K": "$250K+",
};

const INDUSTRY_COLORS: Record<IndustryCode, string> = {
  Construction: "hsl(var(--chart-1))",
  "Transportation & Trucking": "hsl(var(--chart-2))",
  Manufacturing: "hsl(var(--chart-3))",
  "Medical & Healthcare": "hsl(var(--chart-5))",
  "Waste Management": "color-mix(in oklch, hsl(var(--chart-2)) 55%, hsl(var(--foreground)))",
  "Restaurant & Food Service": "hsl(var(--chart-4))",
};

/** Default view: small-ticket vs. everything else. */
export function AgreementRateSplitChart({ data }: { data: AgreementRatePoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval="preserveStartEnd" />
        <YAxis tick={AXIS} stroke="hsl(var(--border))" domain={[40, 100]} tickFormatter={pct} />
        <Tooltip contentStyle={TOOLTIP} formatter={(value: number) => pct(value)} />
        <Legend wrapperStyle={LEGEND} />
        <Line
          type="monotone"
          dataKey="agreementRateSmallTicket"
          name="Small-ticket agreement rate"
          stroke="var(--amber)"
          strokeWidth={2.4}
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="agreementRateRest"
          name="Rest of book"
          stroke="hsl(var(--chart-3))"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/** Alternate view: one series per ticket bucket. */
export function AgreementRateByBucketChart({ data }: { data: AgreementRateByBucketPoint[] }) {
  const flat = data.map((point) => ({ period: point.period, ...point.byBucket }));

  return (
    <ResponsiveContainer width="100%" height={340}>
      <LineChart data={flat} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval="preserveStartEnd" />
        <YAxis tick={AXIS} stroke="hsl(var(--border))" domain={[40, 100]} tickFormatter={pct} />
        <Tooltip contentStyle={TOOLTIP} formatter={(value: number) => pct(value)} />
        <Legend wrapperStyle={LEGEND} />
        {TICKET_BUCKETS.map((bucket) => (
          <Line
            key={bucket}
            type="monotone"
            dataKey={bucket}
            name={BUCKET_LABELS[bucket]}
            stroke={BUCKET_COLORS[bucket]}
            strokeWidth={bucket === "0-10K" ? 2.4 : 1.8}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/** Section 2: agreement rate by industry — the drift diagnostic. */
export function CreditCultureChart({ series }: { series: CreditCultureSeries[] }) {
  const labels = series[0]?.trend.map((point) => point.period) ?? [];
  const data = labels.map((period, index) => {
    const row: Record<string, string | number> = { period };
    for (const entry of series) {
      row[entry.industryCode] = entry.trend[index]?.agreementRate ?? 0;
    }
    return row;
  });

  return (
    <ResponsiveContainer width="100%" height={360}>
      <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis dataKey="period" tick={AXIS} stroke="hsl(var(--border))" interval="preserveStartEnd" />
        <YAxis tick={AXIS} stroke="hsl(var(--border))" domain={[50, 100]} tickFormatter={pct} />
        <Tooltip contentStyle={TOOLTIP} formatter={(value: number) => pct(value)} />
        <Legend wrapperStyle={LEGEND} />
        {series.map((entry) => (
          <Line
            key={entry.industryCode}
            type="monotone"
            dataKey={entry.industryCode}
            name={entry.industryCode}
            stroke={INDUSTRY_COLORS[entry.industryCode]}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
